import { apiGet } from './api';

export function getApiBase() {
  return process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337/api';
}

// Strapi v4 wraps repeatable-component arrays as { data: [{ attributes }] };
// v5 returns the flat array directly. Normalizes either shape to a flat array.
export function normalizeComponentArray(value: unknown): any[] {
  if (!value) return [];
  const v = value as any;
  if (Array.isArray(v.data)) {
    return v.data.map((entry: any) => entry?.attributes || entry).filter(Boolean);
  }
  return Array.isArray(v) ? v : [];
}

export type NormalizedCharity = {
  id?: number | string;
  Title?: string | null;
  slug?: string | null;
  URL?: string | null;
};

export type NormalizedCategory = {
  id: number | string;
  name?: string | null;
  slug?: string | null;
};

export type NormalizedSubmission = {
  id: number | string;
  Slug?: string | null;
  Title?: string | null;
  externalThumbnailUrl?: string | null;
  oembed?: any;
  charity?: NormalizedCharity | null;
  categories?: NormalizedCategory[];
  description?: any;
  phase?: string | null;
  featuredAt?: string | null;
  contentWarning?: string | null;
};

export function normalizeSubmission(raw: any): NormalizedSubmission {
  const s = raw?.attributes ? { id: raw.id, ...(raw.attributes || {}) } : (raw || {});

  const charityData =
    s.charity?.data?.attributes ||
    s.charity ||
    raw?.attributes?.charity?.data?.attributes ||
    raw?.charity?.data?.attributes ||
    null;

  const charityId =
    s.charity?.data?.id ||
    s.charity?.id ||
    raw?.attributes?.charity?.data?.id ||
    raw?.charity?.data?.id ||
    undefined;

  const charity: NormalizedCharity | null = charityData
    ? {
      id: charityId,
      Title: charityData.Title || charityData.name || charityData.title || null,
      slug: (charityData as any).slug || null,
      URL: charityData.URL || charityData.url || null,
    }
    : null;

  // Categories normalization (supports both Strapi v4 nested and flat shapes)
  const categoriesRaw =
    s.categories?.data ||
    s.categories ||
    raw?.attributes?.categories?.data ||
    raw?.categories?.data ||
    [];
  const categories = Array.isArray(categoriesRaw)
    ? categoriesRaw.map((c: any) => {
      const ca = c?.attributes ? { id: c.id, ...(c.attributes || {}) } : c;
      return {
        id: ca.id ?? c.id,
        name: ca.name || ca.Title || ca.title || null,
        slug: ca.slug || null,
      } as NormalizedCategory;
    })
    : [];

  const description = s.description ?? s.Description ?? raw?.attributes?.description ?? null;

  const contentWarning = s.contentWarning ?? raw?.attributes?.contentWarning ?? null;

  return {
    id: s.id ?? raw.id,
    Slug: s.Slug || s.slug || raw.documentId || raw.id,
    Title: s.oembed?.oembed?.title || s.Title || s.title || null,
    externalThumbnailUrl: s.externalThumbnailUrl || null,
    oembed: s.oembed || null,
    charity,
    categories,
    description,
    phase: s.Phase,
    featuredAt: s.featuredAt || (raw?.attributes?.featuredAt ?? null),
    contentWarning,
  };
}

export async function fetchFeaturedSubmissions({
  page = 1,
  pageSize = 25,
  sort = ['featuredAt:desc', 'isUpdated:desc'],
}: {
  page?: number;
  pageSize?: number;
  sort?: string | string[];
}) {
  const base = getApiBase();
  const params = new URLSearchParams();
  params.set('filters[isFeatured][$eq]', 'true');
  params.set('filters[Phase][$eq]', 'Approved');
  params.set('filters[featuredAt][$notNull]', 'true');
  params.set('pagination[page]', String(page));
  params.set('pagination[pageSize]', String(pageSize));
  // Request only the fields needed for featured display (exclude Description richtext)
  params.set('fields[0]', 'Title');
  params.set('fields[1]', 'Slug');
  params.set('fields[2]', 'externalThumbnailUrl');
  params.set('fields[3]', 'Phase');
  params.set('fields[4]', 'featuredAt');
  params.set('fields[5]', 'publishedAt');
  params.set('fields[6]', 'isFeatured');
  params.set('fields[7]', 'isUpdated');
  params.set('fields[8]', 'oembed');
  params.set('fields[9]', 'contentWarning');
  params.set('populate[charity]', 'true');
  const sorts = Array.isArray(sort) ? sort : [sort];
  sorts
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .forEach((value, index) => {
      params.set(`sort[${index}]`, value);
    });
  const url = `${base}/submissions?${params.toString()}`;
  const res = await apiGet(url);
  if (!res.ok) throw new Error('Failed to fetch featured submissions');
  const json = await res.json();
  const list: any[] = json?.data ?? [];
  return list.map(normalizeSubmission);
}

export async function fetchSubmissionsSearch({
  page,
  pageSize,
  sort = 'publishedAt:desc',
  category,
  charityId,
  search,
  searchParam,
  seed,
}: {
  page: number;
  pageSize: number;
  sort?: string;
  category?: string;
  charityId?: string | number | '';
  search?: string;
  searchParam?: string;
  seed?: number;
}) {
  const base = getApiBase();
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  if (sort) params.set('sort', sort);
  if (seed) params.set('seed', String(seed));
  if (category) params.set('category', category);
  if (charityId) params.set('charityId', String(charityId));
  const searchFinal = search ?? searchParam;
  if (searchFinal && searchFinal.trim() !== '') {
    // Set multiple keys for backend compatibility
    params.set('q', searchFinal);
    params.set('searchParam', searchFinal);
    params.set('search', searchFinal);
  }

  // Only show approved videos
  params.set('phase', 'Approved');
  // Request only the fields needed for grid display (exclude Description richtext)
  params.set('fields[0]', 'Title');
  params.set('fields[1]', 'Slug');
  params.set('fields[2]', 'externalThumbnailUrl');
  params.set('fields[3]', 'Phase');
  params.set('fields[4]', 'featuredAt');
  params.set('fields[5]', 'publishedAt');
  params.set('fields[6]', 'oembed');
  params.set('populate[charity]', 'true');

  const url = `${base}/submissions/search?${params.toString()}`;
  const res = await apiGet(url);
  if (!res.ok) throw new Error('Failed to load submissions');
  const json = await res.json();
  const list: any[] = json?.data ?? [];
  return {
    data: list.map(normalizeSubmission),
    meta: json?.meta ?? {},
  };
}
